import type { ErrorCategory } from '../types';

/**
 * Initialization failures use neutral helper text under each field (Figma: grey, no alert icon).
 * Switch failures use {@link audioDeviceStrings.errors.deviceSwitchGeneric} with error styling.
 */
export type InitializationErrorKind = 'access' | 'device' | 'environment';

/** Browser blocked access (e.g. permission prompt denied). */
export const INITIALIZATION_ACCESS_CATEGORIES: readonly ErrorCategory[] = [
  'permission_denied',
];

/** Hardware missing or unavailable because another app is using it. */
export const INITIALIZATION_DEVICE_CATEGORIES: readonly ErrorCategory[] = [
  'device_not_found',
  'device_in_use',
];

/** Media API / generic failures. */
export const INITIALIZATION_ENVIRONMENT_CATEGORIES: readonly ErrorCategory[] = [
  'media_api_unavailable',
  'other',
];

export function getInitializationErrorKind(
  category: ErrorCategory,
): InitializationErrorKind {
  if (INITIALIZATION_ACCESS_CATEGORIES.includes(category)) {
    return 'access';
  }
  if (INITIALIZATION_DEVICE_CATEGORIES.includes(category)) {
    return 'device';
  }
  return 'environment';
}

const initializationMicrophone: Record<ErrorCategory, string> = {
  permission_denied:
    'Please connect a device and allow microphone access for ServiceTitan in your Chrome settings (Privacy and security > Site settings > Microphone).',
  device_not_found: 'No microphone was found. Connect a device and retry.',
  device_in_use:
    'Your microphone is in use by another app. Close it and try again.',
  media_api_unavailable: 'Audio devices are not available in this browser.',
  other: 'Audio devices could not be initialized. Please try again.',
};

const initializationSpeaker: Record<ErrorCategory, string> = {
  permission_denied:
    'Speaker access was denied. Check browser permissions and reload.',
  device_not_found: 'Connect a device and retry.',
  device_in_use:
    'Your speaker is in use by another app. Close it and try again.',
  media_api_unavailable: initializationMicrophone.media_api_unavailable,
  other: initializationMicrophone.other,
};

export const audioDeviceStrings = {
  labels: {
    microphone: 'Input',
    speaker: 'Output',
    ringOutput: 'Ring Output',
  },
  placeholders: {
    selectDevice: 'Select a device',
    loadingDevices: 'Loading devices…',
    /** Shown in combobox triggers when init failed and there are no devices to list */
    noDevicesAvailable: 'No devices available',
    noMicrophoneFound: 'No microphone found',
    noSpeakerDetected: 'No speaker detected',
  },
  toggle: {
    ringOutputLabel: 'Hear incoming call ringtones on a separate device',
    ringOutputTooltip:
      "Select a device for incoming calls to ring through so you don't miss a call if you're away from your desk",
  },
  testButtons: {
    testMicrophone: 'Test Microphone',
    allowMicrophone: 'Allow Microphone',
    listeningMicrophone: 'Listening...',
    testSpeaker: 'Test Speaker',
    playingSpeaker: 'Playing...',
  },
  errors: {
    deviceSwitchGeneric:
      'Could not switch audio device. Please try again or pick another device.',
    speakerTestFailed:
      'Unable to play test sound. Please check your speaker device and try again.',
    unknownPlaybackError: 'Unknown audio playback error',
  },
} as const;

export function getInitializationFieldErrorMessage(
  category: ErrorCategory,
  field: 'microphone' | 'speaker',
): string {
  return field === 'microphone'
    ? initializationMicrophone[category]
    : initializationSpeaker[category];
}
